import React, { useState } from 'react';
import { ChevronDown, Clock, Lock, Shield, Smartphone, Zap, Star, Menu, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import PhoneDemo from '../components/PhoneDemo';
import Testimonials from '../components/Testimonials';
import LanguageSwitcher from '../components/LanguageSwitcher';
import { mockData } from '../mockData';
import { useLanguage } from '../contexts/LanguageContext';
import { getTranslation } from '../translations';

const Home = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { language } = useLanguage();
  const t = (key) => getTranslation(language, key);

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm border-b border-gray-100">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 sm:h-20">
            <div className="flex items-center space-x-2 rtl:space-x-reverse">
              <Smartphone className="w-6 h-6 sm:w-8 sm:h-8" />
              <span className="text-lg sm:text-xl font-semibold">minimalist phone</span>
            </div>
            
            <div className="hidden md:flex items-center space-x-4 rtl:space-x-reverse">
              <a 
                href="https://www.producthunt.com/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center space-x-2 rtl:space-x-reverse px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors"
              >
                <span className="font-semibold">{t('productHunt')}</span>
                <span className="bg-white text-orange-500 px-2 py-1 rounded font-bold text-sm">358</span>
              </a>
              <LanguageSwitcher />
              <Button className="bg-black text-white hover:bg-gray-800">{t('download')}</Button>
            </div>

            <button 
              className="md:hidden"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-gray-100 py-4 px-4">
            <div className="flex flex-col space-y-3">
              <LanguageSwitcher />
              <Button className="bg-black text-white hover:bg-gray-800 w-full">{t('download')}</Button>
            </div>
          </div>
        )}
      </header>

      {/* Hero Section */}
      <section className="pt-24 sm:pt-32 pb-12 sm:pb-20 px-4 sm:px-6 lg:px-8" style={{ backgroundColor: '#8b8c6b' }}>
        <div className="container mx-auto">
          <div className="grid md:grid-cols-2 gap-8 lg:gap-12 items-center">
            <div className="text-white space-y-6">
              <div className="space-y-2">
                <h1 className="text-xs sm:text-sm uppercase tracking-wider opacity-80">{t('heroTitle')}</h1>
                <p className="text-xs sm:text-sm opacity-80">{t('heroSubtitle')}</p>
              </div>
              <a href="#" className="inline-block">
                <img 
                  src="https://cdn.prod.website-files.com/5f36788697194f4ebab0391a/692591ffcfa9e8896ea61cac_Download_on_the_App_Store_Badge_US-UK_RGB_blk_092917%201.png" 
                  alt="Download on App Store" 
                  className="h-12 sm:h-14"
                />
              </a>
            </div>
            <div className="relative">
              <img 
                src="https://cdn.prod.website-files.com/5f36788697194f4ebab0391a/6880fec207416953427151c8_iphone-sofa-more-centered.jpg" 
                alt="iPhone with minimalist phone" 
                className="w-full h-auto rounded-lg"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Main Headline */}
      <section className="py-12 sm:py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="container mx-auto text-center">
          <h2 className="text-4xl sm:text-6xl lg:text-7xl font-light leading-tight">
            {t('mainHeadline1')}<br />
            <span className="font-normal">{t('mainHeadline2')}</span>
          </h2>
        </div>
      </section>

      {/* Android Download Section */}
      <section className="py-12 sm:py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="container mx-auto">
          <div className="grid md:grid-cols-2 gap-8 lg:gap-12 items-center">
            <div className="order-2 md:order-1">
              <img 
                src="https://cdn.prod.website-files.com/5f36788697194f4ebab0391a/64467f3748d74305b4f440bb_minimalist%20phone%20title%20image%20fixed2.jpeg" 
                alt="Minimalist phone on Android" 
                className="w-full h-auto rounded-lg shadow-lg"
              />
            </div>
            <div className="order-1 md:order-2">
              <a href="#" className="inline-block hover:opacity-80 transition-opacity">
                <img 
                  src="https://cdn.prod.website-files.com/5f36788697194f4ebab0391a/5f3cdddd283c841108fc7113_google_play_btn_white.svg" 
                  alt="Download on Google Play" 
                  className="h-14 sm:h-16"
                />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section 1 */}
      <section className="py-12 sm:py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="container mx-auto">
          <div className="max-w-4xl mx-auto">
            <h3 className="text-3xl sm:text-4xl lg:text-5xl font-light mb-6 sm:mb-8 text-center">
              {t('feature1Title')}
            </h3>
            <p className="text-base sm:text-lg text-gray-700 leading-relaxed text-center mb-12">
              {t('feature1Desc')}
            </p>
            <p className="text-sm text-gray-500 text-center mb-12">{t('feature1Note')}</p>
            
            <div className="grid sm:grid-cols-2 gap-6 sm:gap-8 max-w-2xl mx-auto">
              <div className="bg-black rounded-2xl p-6 sm:p-8 text-white">
                <div className="text-center mb-6">
                  <div className="text-4xl sm:text-5xl font-light mb-2">15:07</div>
                  <div className="text-sm opacity-80">Tuesday, 26th May</div>
                </div>
                <div className="space-y-4">
                  {[t('calendar'), t('maps'), t('mail'), t('podcasts')].map((app) => (
                    <div key={app} className="text-center py-3 border-b border-gray-700 hover:bg-gray-800 transition-colors rounded cursor-pointer">{app}</div>
                  ))}
                </div>
              </div>
              <div className="bg-black rounded-2xl p-6 sm:p-8 text-white">
                <div className="text-center mb-6">
                  <div className="text-lg font-semibold mb-4">{t('allApps')}</div>
                </div>
                <div className="space-y-3 text-sm">
                  {[t('calendar'), t('camera'), t('chrome'), t('clock'), t('contacts'), t('drive'), t('gmail'), t('keep'), t('maps')].map((app) => (
                    <div key={app} className="text-center py-2 hover:bg-gray-800 transition-colors rounded cursor-pointer">{app}</div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section 2 */}
      <section className="py-12 sm:py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="container mx-auto">
          <div className="max-w-4xl mx-auto text-center">
            <h3 className="text-3xl sm:text-4xl lg:text-5xl font-light mb-6 sm:mb-8">
              {t('feature2Title')}
            </h3>
            <p className="text-base sm:text-lg text-gray-700 leading-relaxed">
              {t('feature2Desc')}
            </p>
          </div>
        </div>
      </section>

      {/* Features Section 3 */}
      <section className="py-12 sm:py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="container mx-auto">
          <div className="max-w-4xl mx-auto">
            <h3 className="text-3xl sm:text-4xl lg:text-5xl font-light mb-6 sm:mb-8 text-center">
              {t('feature3Title')}
            </h3>
            <p className="text-base sm:text-lg text-gray-700 leading-relaxed text-center mb-12">
              {t('feature3Desc')}
            </p>
            
            <div className="grid sm:grid-cols-2 gap-6 sm:gap-8 max-w-2xl mx-auto">
              <div className="bg-white border-2 border-gray-200 rounded-2xl p-6 sm:p-8">
                <div className="text-center mb-6">
                  <div className="text-4xl sm:text-5xl font-light mb-2">15:07</div>
                  <div className="text-sm text-gray-600">Tuesday, 26th May</div>
                </div>
                <div className="space-y-4">
                  {[t('calendar'), t('maps'), t('mail'), t('podcasts')].map((app) => (
                    <div key={app} className="text-center py-3 border-b border-gray-200 hover:bg-gray-50 transition-colors rounded cursor-pointer">{app}</div>
                  ))}
                </div>
              </div>
              <div className="bg-white border-2 border-gray-200 rounded-2xl p-6 sm:p-8">
                <div className="text-center mb-6">
                  <div className="text-lg font-semibold mb-4">{t('allApps')}</div>
                </div>
                <div className="space-y-3 text-sm">
                  {[t('calendar'), t('camera'), t('chrome'), t('clock'), t('contacts'), t('drive'), t('gmail'), t('keep'), t('maps')].map((app) => (
                    <div key={app} className="text-center py-2 hover:bg-gray-50 transition-colors rounded cursor-pointer">{app}</div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Interactive Phone Demo - NEW FEATURE */}
      <section className="py-12 sm:py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-gray-50 to-white">
        <div className="container mx-auto">
          <div className="max-w-4xl mx-auto text-center mb-12">
            <h3 className="text-3xl sm:text-4xl lg:text-5xl font-light mb-6 sm:mb-8">
              {t('lockFeatureTitle')}
            </h3>
            <p className="text-base sm:text-lg text-gray-700 leading-relaxed">
              {t('lockFeatureDesc')}
            </p>
          </div>
          <PhoneDemo />
        </div>
      </section>

      {/* Use Your Phone Section */}
      <section className="py-12 sm:py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="container mx-auto text-center">
          <h3 className="text-3xl sm:text-4xl lg:text-5xl font-light">
            {t('mindfulTitle')}<br />
            <span className="font-normal">{t('mindfulTitle2')}</span>
          </h3>
          <p className="text-base sm:text-lg text-gray-700 leading-relaxed mt-6 sm:mt-8 max-w-3xl mx-auto">
            {t('mindfulDesc')}
          </p>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-12 sm:py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="container mx-auto">
          <Testimonials testimonials={mockData.testimonials[language]} />
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-12 sm:py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="container mx-auto">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-4xl sm:text-5xl font-light text-center mb-12 sm:mb-16">{t('faqTitle')}</h2>
            <Accordion type="single" collapsible className="space-y-4">
              {mockData.faqs[language].map((faq) => (
                <AccordionItem key={faq.question} value={faq.question} className="bg-white rounded-lg border border-gray-200 px-6">
                  <AccordionTrigger className="text-left text-base sm:text-lg font-medium hover:no-underline py-6">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-gray-700 pb-6">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
            <div className="text-center mt-8">
              <a href="#" className="text-lg text-blue-600 hover:underline">
                {t('faqLink')}
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-12 sm:py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="container mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-light mb-8">
              {t('finalCTA')}
            </h2>
            <a href="#" className="inline-block hover:opacity-80 transition-opacity">
              <img 
                src="https://cdn.prod.website-files.com/5f36788697194f4ebab0391a/5f3cdddd283c841108fc7113_google_play_btn_white.svg" 
                alt="Download on Google Play" 
                className="h-14 sm:h-16"
              />
            </a>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { id: 'product-2', url: 'https://cdn.prod.website-files.com/5f36788697194f4ebab0391a/5fe5fc9c98626f41128d89b4_minimalist%20phone%20product%202.jpg' },
              { id: 'product-1', url: 'https://cdn.prod.website-files.com/5f36788697194f4ebab0391a/5fe5fc9cc90573a4af9e58eb_minimalist%20phone%20product%201.jpg' },
              { id: 'product-3', url: 'https://cdn.prod.website-files.com/5f36788697194f4ebab0391a/5fe5fc9b98626fea478d89b3_minimalist%20phone%20product%203.jpg' },
              { id: 'product-4', url: 'https://cdn.prod.website-files.com/5f36788697194f4ebab0391a/605f3f13de5cb487492ce023_MINapp_Bild4_black1_resized.JPG' }
            ].map((img) => (
              <div key={img.id} className="aspect-square overflow-hidden rounded-lg">
                <img src={img.url} alt={img.id} className="w-full h-full object-cover hover:scale-105 transition-transform duration-300" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-black text-white py-12 px-4 sm:px-6 lg:px-8">
        <div className="container mx-auto">
          <div className="grid md:grid-cols-3 gap-8 text-center md:text-left">
            <div>
              <div className="flex items-center justify-center md:justify-start space-x-2 rtl:space-x-reverse mb-4">
                <Smartphone className="w-6 h-6" />
                <span className="text-lg font-semibold">{t('appName')}</span>
              </div>
              <p className="text-sm text-gray-400">{t('footerTagline')}</p>
              <p className="text-xs text-gray-500 mt-2">{t('domain')}</p>
            </div>
            <div>
              <h4 className="font-semibold mb-4">{t('footerLinks')}</h4>
              <ul className="space-y-2 text-sm text-gray-400">
                <li><a href="#" className="hover:text-white transition-colors">{t('footerFaqs')}</a></li>
                <li><a href="#" className="hover:text-white transition-colors">{t('footerPrivacy')}</a></li>
                <li><a href="#" className="hover:text-white transition-colors">{t('footerTerms')}</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">{t('footerDownload')}</h4>
              <div className="space-y-3">
                <a href="#" className="block">
                  <img 
                    src="https://cdn.prod.website-files.com/5f36788697194f4ebab0391a/5f3cdddd283c841108fc7113_google_play_btn_white.svg" 
                    alt="Google Play" 
                    className="h-10 mx-auto md:mx-0"
                  />
                </a>
              </div>
            </div>
          </div>
          <div className="border-t border-gray-800 mt-8 pt-8 text-center text-sm text-gray-400">
            <p>{t('footerCopyright')}</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;
